import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { PackingApiAdapter } from '../adapters/PackingApiAdapter';
import { PackingProduct, PackingData, Order } from '../models';
import { CommonSortType, Location } from '@modules/OrderPreparation/shared';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useParams } from 'react-router-dom';

export interface PackingContextType {
  data: PackingData | null;
  products: PackingProduct[];
  orders: Order[];
  ordersLoading: boolean;
  ordersError: string | null;
  loading: boolean;
  error: string | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredProducts: PackingProduct[];
  sortType: CommonSortType | null;
  handleSort: (sortType: CommonSortType) => void;
  getProductLocations: (productId: number) => Promise<Location[]>;
  getOrders: () => Promise<void>;
  refetchData: () => Promise<void>;
  adapter: PackingApiAdapter;
}

const PackingContext = createContext<PackingContextType | undefined>(undefined);

// Exportar el contexto para que pueda ser usado por el hook
export { PackingContext };

// El hook usePackingContext ahora está en hooks/usePackingContext.ts

interface PackingProviderProps {
  children: React.ReactNode;
}

export const PackingProvider: React.FC<PackingProviderProps> = ({
  children,
}) => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PackingData | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortType, setSortType] = useState<CommonSortType | null>(null);

  // Crear instancia del adaptador usando useMemo para evitar recreaciones
  const adapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new PackingApiAdapter(httpClient);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('ID de preparación no proporcionado');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await adapter.getPackingData(parseInt(id));

        setData(result);
      } catch (err: any) {
        // Si es error 404, redirigir a página no encontrada
        if (err.message === '404 - Preparación no encontrada') {
          // Intentar múltiples métodos de redirección
          try {
            window.location.href = '/404';
          } catch {
            window.location.replace('/404');
          }
          return;
        }
        setError(err.message || 'Error al cargar los datos del packing');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adapter, id]);

  // Obtener productos del data, sin aplicar cantidades asignadas desde localStorage
  const products = useMemo(() => {
    if (!data?.products) return [];

    return data.products.map((product) => ({
      ...product,
      // Las cantidades procesadas vienen directamente de la API
      processedQuantity: product.processedQuantity || 0,
      isCompleted: (product.processedQuantity || 0) >= product.expectedQuantity,
    }));
  }, [data?.products]);

  // Filtrar y ordenar productos usando useMemo
  const filteredProducts = useMemo(() => {
    let filtered =
      products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.code.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

    // Aplicar ordenamiento si está seleccionado
    if (sortType) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortType) {
          case 'asc':
            return a.name.localeCompare(b.name);
          case 'desc':
            return b.name.localeCompare(a.name);
          case 'recent':
            // Ordenar por cantidad procesada (más unidades = más procesado)
            return b.processedQuantity - a.processedQuantity;
          case 'units_asc':
            // Ordenar por cantidad procesada (menos unidades = menos procesado)
            return a.processedQuantity - b.processedQuantity;
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [products, searchTerm, sortType]);

  // Handler para ordenar productos usando useCallback
  const handleSort = useCallback((newSortType: CommonSortType) => {
    setSortType(newSortType);
  }, []);

  // Handler para obtener ubicaciones de un producto
  const getProductLocations = useCallback(
    async (productId: number): Promise<Location[]> => {
      return await adapter.getLocationsByProductId(productId);
    },
    [adapter]
  );

  // Handler para obtener pedidos
  const getOrders = useCallback(async () => {
    if (!id) {
      setOrdersError('ID de preparación no proporcionado');
      return;
    }

    try {
      setOrdersLoading(true);
      setOrdersError(null);

      const ordersData = await adapter.getOrdersByPreparationId(parseInt(id));

      setOrders(ordersData);
    } catch (err: any) {
      setOrdersError(err.message || 'Error al cargar los pedidos');
    } finally {
      setOrdersLoading(false);
    }
  }, [adapter, id]);

  // Handler para recargar todos los datos
  const refetchData = useCallback(async () => {
    if (!id) {
      setError('ID de preparación no proporcionado');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await adapter.getPackingData(parseInt(id));
      setData(result);

      // También recargar las órdenes
      const ordersData = await adapter.getOrdersByPreparationId(parseInt(id));
      setOrders(ordersData);
    } catch (err: any) {
      setError(err.message || 'Error al recargar los datos del packing');
    } finally {
      setLoading(false);
    }
  }, [adapter, id]);

  const value: PackingContextType = {
    data,
    products,
    orders,
    ordersLoading,
    ordersError,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filteredProducts,
    sortType,
    handleSort,
    getProductLocations,
    getOrders,
    refetchData,
    adapter,
  };

  return (
    <PackingContext.Provider value={value}>{children}</PackingContext.Provider>
  );
};
